import React from 'react';
import styled from 'styled-components';
import {
  Button,
  Heading,
  Modal,
} from '@alefeducation/ui-elements-library-core';
import { Times } from '@alefeducation/ui-elements-library-icons';
import {
  CustomRolesImportSummary,
  formatImportSummaryTitle,
} from './utils/importCustomRolesSummary';
import {
  IMPORT_CUSTOM_ROLES_SUMMARY_TITLE,
  IMPORT_ROLE_CREATED,
  IMPORT_ROLE_FAILED,
  IMPORT_ROLE_UPDATED,
} from '../../../utils/constants';

type Props = {
  summary: CustomRolesImportSummary;
  onClose: () => void;
};

const statusLabels = {
  created: IMPORT_ROLE_CREATED,
  updated: IMPORT_ROLE_UPDATED,
  failed: IMPORT_ROLE_FAILED,
};

const ImportCustomRolesSummaryModal = ({ summary, onClose }: Props) => {
  return (
    <Modal open size="medium">
      <SummaryModalContainer>
        <CloseIcon>
          <Times
            className="import-summary-modal__close-icon"
            color="primary.80"
            data-testid="close-import-summary-modal"
            height={14}
            width={14}
            onClick={onClose}
          />
        </CloseIcon>
        <SummaryModalContent>
          <Heading color="text100" variant="heading-small" weight="bold">
            {IMPORT_CUSTOM_ROLES_SUMMARY_TITLE}
          </Heading>
          <SummaryStats data-testid="import-summary-stats">
            {formatImportSummaryTitle(summary)}
          </SummaryStats>
          <ResultsList data-testid="import-summary-results">
            {summary.results.map((result) => (
              <ResultItem
                key={`${result.roleUuid || 'new'}-${result.roleName}`}
                data-testid={`import-result-${result.roleName}`}
              >
                <ResultHeader>
                  <RoleName>{result.roleName}</RoleName>
                  <StatusBadge $status={result.status}>
                    {statusLabels[result.status]}
                  </StatusBadge>
                </ResultHeader>
                {result.roleUuid ? (
                  <RoleMeta>UUID: {result.roleUuid}</RoleMeta>
                ) : null}
                <ResultMessage $status={result.status}>
                  {result.message}
                </ResultMessage>
              </ResultItem>
            ))}
          </ResultsList>
        </SummaryModalContent>
        <SummaryModalActions>
          <Button
            variant="primary"
            size="sm"
            data-testid="bt-import-summary-close"
            onClick={onClose}
          >
            Close
          </Button>
        </SummaryModalActions>
      </SummaryModalContainer>
    </Modal>
  );
};

export default ImportCustomRolesSummaryModal;

const SummaryModalContainer = styled.div`
  padding: 16px 0 40px 0;
`;

const CloseIcon = styled.div`
  height: 14px;
  position: absolute;
  right: 15px;
  cursor: pointer;
`;

const SummaryModalContent = styled.div`
  border-bottom: 1px solid $break-line;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 420px;
  overflow-y: auto;
  padding: 24px 40px;
`;

const SummaryStats = styled.div`
  color: ${({ theme }: any) => theme.colors.primary[80]};
  font-size: 14px;
  font-weight: 700;
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ResultItem = styled.div`
  border: 1px solid ${({ theme }: any) => theme.colors.primary[10]};
  border-radius: 8px;
  padding: 12px;
`;

const ResultHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const RoleName = styled.div`
  font-size: 14px;
  font-weight: 700;
`;

const RoleMeta = styled.div`
  color: ${({ theme }: any) => theme.colors.primary[60]};
  font-size: 12px;
  margin-top: 4px;
`;

const StatusBadge = styled.span<{ $status: 'created' | 'updated' | 'failed' }>`
  background-color: ${({ $status, theme }: any) =>
    $status === 'failed' ? theme.colors.error[10] : theme.colors.success[10]};
  border-radius: 12px;
  color: ${({ $status, theme }: any) =>
    $status === 'failed' ? theme.colors.error[100] : theme.colors.success[100]};
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  white-space: nowrap;
`;

const ResultMessage = styled.div<{ $status: 'created' | 'updated' | 'failed' }>`
  color: ${({ $status, theme }: any) =>
    $status === 'failed' ? theme.colors.error[100] : theme.colors.primary[80]};
  font-size: 13px;
  margin-top: 8px;
`;

const SummaryModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 24px 40px 0;

  button {
    height: 40px;
    width: 146px;
  }
`;
